import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Loader2 } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/shared/components/ui/sheet'
import { Button } from '@/shared/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form'
import { Input } from '@/shared/components/ui/input'
import { Textarea } from '@/shared/components/ui/textarea'
import { Switch } from '@/shared/components/ui/switch'
import { useUpdateCampania } from '@/modules/multichannel/hooks/useCampanias'
import type { CampaniaDetalle } from '@/modules/multichannel/types/campania'

const campaniaFormSchema = z.object({
  nombre_campania: z.string().min(1, 'El nombre de la campaña es requerido'),
  target: z.string().min(1, 'El target es requerido'),
  base_segmentada: z.string().min(1, 'La base segmentada es requerida'),
  remitente: z.string().min(1, 'El remitente es requerido'),
  asunto: z.string().nullable(),
  pre_encabezado: z.string().nullable(),
  titulo_envio: z.string().nullable(),
  contenido_html: z.string().nullable(),
  notas: z.string().nullable(),
  fecha_publicacion: z.string().nullable(),
  hora: z.string().nullable(),
  requiere_aprobacion: z.boolean(),
})

type CampaniaFormValues = z.infer<typeof campaniaFormSchema>

interface CampaniaEditFormProps {
  campania: CampaniaDetalle
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export function CampaniaEditForm({
  campania,
  open,
  onOpenChange,
  onSuccess,
}: CampaniaEditFormProps) {
  const updateCampania = useUpdateCampania()

  const form = useForm<CampaniaFormValues>({
    resolver: zodResolver(campaniaFormSchema),
    defaultValues: {
      nombre_campania: campania.nombre_campania,
      target: campania.target,
      base_segmentada: campania.base_segmentada,
      remitente: campania.remitente,
      asunto: campania.asunto || '',
      pre_encabezado: campania.pre_encabezado || '',
      titulo_envio: campania.titulo_envio || '',
      contenido_html: campania.contenido_html || '',
      notas: campania.notas || '',
      fecha_publicacion: campania.fecha_publicacion || '',
      hora: campania.hora || '',
      requiere_aprobacion: campania.requiere_aprobacion,
    },
  })

  // Reset form when campania changes
  useEffect(() => {
    form.reset({
      nombre_campania: campania.nombre_campania,
      target: campania.target,
      base_segmentada: campania.base_segmentada,
      remitente: campania.remitente,
      asunto: campania.asunto || '',
      pre_encabezado: campania.pre_encabezado || '',
      titulo_envio: campania.titulo_envio || '',
      contenido_html: campania.contenido_html || '',
      notas: campania.notas || '',
      fecha_publicacion: campania.fecha_publicacion || '',
      hora: campania.hora || '',
      requiere_aprobacion: campania.requiere_aprobacion,
    })
  }, [campania, form])

  const onSubmit = async (values: CampaniaFormValues) => {
    try {
      // Convertir strings vacíos a null
      const data = {
        ...values,
        asunto: values.asunto || null,
        pre_encabezado: values.pre_encabezado || null,
        titulo_envio: values.titulo_envio || null,
        contenido_html: values.contenido_html || null,
        notas: values.notas || null,
        fecha_publicacion: values.fecha_publicacion || null,
        hora: values.hora || null,
      }

      await updateCampania.mutateAsync({
        id: campania.id,
        data,
      })

      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      console.error('Error al actualizar campaña:', error)
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[600px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Editar Campaña</SheetTitle>
          <SheetDescription>
            Actualiza la información de la campaña {campania.nombre_campania}
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 mt-6">
            {/* Nombre de Campaña */}
            <FormField
              control={form.control}
              name="nombre_campania"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre de Campaña *</FormLabel>
                  <FormControl>
                    <Input placeholder="Nombre de la campaña" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Target */}
            <FormField
              control={form.control}
              name="target"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Target *</FormLabel>
                  <FormControl>
                    <Input placeholder="Target de la campaña" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Base Segmentada */}
            <FormField
              control={form.control}
              name="base_segmentada"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Base Segmentada *</FormLabel>
                  <FormControl>
                    <Input placeholder="Base segmentada" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Remitente */}
            <FormField
              control={form.control}
              name="remitente"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Remitente *</FormLabel>
                  <FormControl>
                    <Input placeholder="Remitente del email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Asunto */}
            <FormField
              control={form.control}
              name="asunto"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Asunto</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Asunto del email"
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Pre-encabezado */}
            <FormField
              control={form.control}
              name="pre_encabezado"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pre-encabezado</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Pre-encabezado del email"
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Título del Envío */}
            <FormField
              control={form.control}
              name="titulo_envio"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Título del Envío</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Título del envío"
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Contenido HTML */}
            <FormField
              control={form.control}
              name="contenido_html"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contenido HTML</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Contenido HTML del email"
                      className="min-h-[200px] font-mono text-xs"
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Fecha de Publicación */}
            <FormField
              control={form.control}
              name="fecha_publicacion"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fecha de Publicación</FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Hora */}
            <FormField
              control={form.control}
              name="hora"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Hora</FormLabel>
                  <FormControl>
                    <Input
                      type="time"
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Notas */}
            <FormField
              control={form.control}
              name="notas"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notas</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Notas adicionales"
                      className="min-h-[100px]"
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Requiere Aprobación */}
            <FormField
              control={form.control}
              name="requiere_aprobacion"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      Requiere Aprobación
                    </FormLabel>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Botones */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={updateCampania.isPending}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={updateCampania.isPending}>
                {updateCampania.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Guardar Cambios
              </Button>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}
